import { makeStyles } from '@material-ui/styles'
import { TablePrefsParams, listTablePrefs } from 'app/constants'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import UsageProgress from 'k8s/components/capacity-and-health/UsageProgress'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { snakeToPascalString } from 'utils/misc'
import { listVmHaClusters } from '../actions'
import { listClusterHostGroups } from './actions'
import CreateClusterHostGroupsModal from './CreateClusterHostGroupsModal'
import DeleteClusterHostGroupsDialog from './DeleteClusterHostGroupsDialog'
import EditClusterHostGroupModal from './EditClusterHostGroupModal'
import { clusterHostGroupsSelector } from './selectors'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import { formatTimestampToLocal } from '../../vms/helpers'

type ModelDataKey = DataKeys.ClusterHostGroups
type SelectorModel = ArrayElement<ReturnType<typeof clusterHostGroupsSelector>>
type ActionParams = InferActionParams<typeof listClusterHostGroups>

type Params = ActionParams & {}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ClusterHostGroups',
  listTablePrefs,
)

const defaultParams: Params & SortingState = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

// This component is used to display the enabled or disabled status of a cluster host group
export const EnabledOrDisabled = (props) => {
  const classes = useStyles({ enabled: props.value })
  return (
    <Text variant="body2" className={classes.circle}>
      {props.value ? 'Enabled' : 'Disabled'}
    </Text>
  )
}

const renderIcon = (status) => {
  const classes = useStyles({})
  switch (status) {
    case 'creating':
    case 'migrating':
    case 'removing':
    case 'loading':
      return (
        <FontAwesomeIcon className={classes.marginRight} spin>
          sync
        </FontAwesomeIcon>
      )
    default:
      return null
  }
}
export const VmHaStatus = (props: { value: string; item: SelectorModel }) => {
  const classes = useStyles({ status: props?.value })
  const icon = renderIcon(props?.value)
  const circle = (
    <Text variant="body2" className={!icon ? classes.vmhaStatus : undefined}>
      {icon}
      {props?.value && snakeToPascalString(props?.value)}
    </Text>
  )

  const tooltip = (
    <div className={classes.vmhaStatusTooltipwrapper}>
      <Tooltip
        message={
          props?.value === 'waiting'
            ? 'Waiting for minimum two hosts to be assigned to the cluster'
            : props?.value === 'pending'
            ? 'Waiting for VMHA to be enabled'
            : null
        }
      >
        {circle}
      </Tooltip>
    </div>
  )

  return props?.value === 'waiting' || props?.value === 'pending' ? tooltip : circle
}

const modeString = {
  passthrough: 'Passthrough',
  vgpu: 'vGPU',
}

export const GpuCellComponent = ({ value, item }) => {
  const classes = useStyles({ enabled: value })
  return (
    <>
      <Text variant="body2" className={classes.circle}>
        {value ? `Enabled (${modeString[item?.gpu?.mode]})` : 'Disabled'}
      </Text>
    </>
  )
}

export const modeLabels = {
  custom: 'Custom',
  'host-model': 'Host Model',
  'host-passthrough': 'Host Passthrough',
}

const ClusterHostGroupsListPage = () => {
  const { loading, reload, refreshing: clusterHostGroupsRefreshing } = useListAction(
    listClusterHostGroups,
  )
  const { loading: vmhaLoading, reload: vmhaReload } = useListAction(listVmHaClusters)

  const clusterHostGroups = useSelector(clusterHostGroupsSelector)
  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const [showEditClusterHostGroupModal, setShowEditClusterHostGroupModal] = useState<boolean>(false)
  const [showDeleteClusterHostGroupModal, setShowDeleteClusterHostGroupModal] = useState<boolean>(
    false,
  )
  const [selectedClusterHostGroup, setSelectedClusterHostGroup] = useState<SelectorModel[] | null>(
    null,
  )

  const columns: GridViewColumn<SelectorModel>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        CellComponent: ({ value, item }) => (
          <SimpleLink src={routeManager.routes.openstack.clusterDetails.path({ id: item?.name })}>
            {((<>{value}</>) as unknown) as React.ReactNode}
          </SimpleLink>
        ),
      },
      // {
      //   key: 'status',
      //   label: 'Status',
      //   CellComponent: ({ value, item }) => (
      //     <Badge variant={getClusterHostGroupBadgeVariant(item?.status)} text={value} />
      //   ),
      // },
      {
        key: 'hostlist.length',
        label: 'Hosts',
        CellComponent: ({ value = '0' }) => <Text variant="body2">{value as string}</Text>,
      },
      {
        key: 'physicalNetworkLabels',
        label: 'Physical Network Label',
        display: false,
        CellComponent: (props) => {
          const physicalNetworkLabels = (props?.value as string[]) || []
          return <Text variant="body2"> {physicalNetworkLabels?.join(', ')}</Text>
        },
      },
      {
        key: 'vmHaState',
        label: 'VMHA',
        CellComponent: ({ value, item }) => (
          <VmHaStatus value={vmhaLoading ? 'loading' : (value as string)} item={item} />
        ),
      },
      {
        key: 'autoResourceRebalancing.enabled',
        label: 'Dynamic Resource Rebalancing',
        CellComponent: EnabledOrDisabled,
      },
      {
        key: 'gpu.enabled',
        label: 'GPU',
        CellComponent: GpuCellComponent,
      },
      {
        key: 'cpu.mode',
        label: 'Virtualized Guest CPU Mode',
        render: (value: string) => {
          return modeLabels[value] || 'Default'
        },
        display: false,
      },
      {
        key: 'cpu.model',
        label: 'Virtualized Guest CPU Model',
        display: false,
      },
      {
        key: 'totalUsage.cpuUsage',
        label: 'CPU Usage (GHz)',
        CellComponent: UsageProgress,
      },
      {
        key: 'totalUsage.memoryUsage',
        label: 'Memory Usage (GiB)',
        CellComponent: UsageProgress,
      },
      {
        key: 'totalUsage.storageUsage',
        label: 'Storage Usage (GiB)',
        CellComponent: UsageProgress,
      },
      {
        key: 'created_at',
        label: 'Created',
        render: (time: string) => formatTimestampToLocal(time),
        display: false,
      },
    ],
    [vmhaLoading],
  )

  const batchActions = [
    {
      cond: (selectedItems) => selectedItems.length === 1,
      disabledTooltip: 'You can only edit one cluster at a time',
      label: 'Edit Cluster',
      icon: 'edit',
      handleAction: (selectedItems) => {
        setSelectedClusterHostGroup(selectedItems)
        setShowEditClusterHostGroupModal(true)
      },
      refreshAfterSuccess: true,
    },
    {
      cond: (selectedItems) => !selectedItems.some((item) => item?.hostlist?.length > 0),
      label: 'Delete Cluster',
      icon: 'trash-alt',
      handleAction: (selectedItems) => {
        setSelectedClusterHostGroup(selectedItems)
        setShowDeleteClusterHostGroupModal(true)
      },
      refreshAfterSuccess: true,
      disabledTooltip: 'Cannot delete a cluster with hosts assigned',
    },
  ]

  const handleReload = useCallback(() => {
    reload(true, false)
    vmhaReload(true, false)
  }, [reload, vmhaReload])

  return (
    <>
      <DocumentMeta title="Clusters" />
      <PollingData
        hidden
        loading={loading}
        onReload={() => {
          return new Promise((resolve) => {
            handleReload()
            resolve()
          })
        }}
        refreshDuration={1000 * 30}
      />
      <CreateClusterHostGroupsModal addRoute={routeManager.routes.openstack.createCluster} />
      {showEditClusterHostGroupModal && (
        <EditClusterHostGroupModal
          onClose={() => setShowEditClusterHostGroupModal(false)}
          rows={selectedClusterHostGroup}
        />
      )}
      {showDeleteClusterHostGroupModal && (
        <DeleteClusterHostGroupsDialog
          onClose={() => setShowDeleteClusterHostGroupModal(false)}
          rows={selectedClusterHostGroup}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        searchTargets={['name']}
        uniqueIdentifier="name"
        loading={loading}
        loadingMessage="Loading Clusters..."
        onRefresh={() => {
          reload(true)
          vmhaReload(true)
        }}
        refreshing={clusterHostGroupsRefreshing}
        multiSelection
        data={clusterHostGroups}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        label="Clusters"
        showItemsCountInLabel
        batchActions={batchActions}
        emptyContent="No clusters found"
        addUrl={routeManager.routes.openstack.createCluster.path()}
        addText="Add Cluster"
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}

export default ClusterHostGroupsListPage

const useStyles = makeStyles<Theme, { status?: string; enabled?: boolean }>((theme: Theme) => {
  const commonStyles = {
    display: 'inline-grid',
    alignItems: 'center',
    gridTemplateColumns: 'minmax(22px, max-content) 1fr',
    whiteSpace: 'nowrap' as const,
  }

  return {
    marginRight: {
      marginRight: 8,
    },
    vmhaStatus: {
      ...commonStyles,
      '&:before': {
        content: '""',
        display: 'inherit',
        height: 12,
        width: 12,
        borderRadius: '50%',
        backgroundColor: ({ status }) => {
          switch (status) {
            case 'completed':
            case 'enabled':
            case 'active':
              return theme.palette.green.main
            case 'error-removing':
            case 'disabled':
              return theme.palette.red.main
            default:
              return theme.palette.orange.main
          }
        },
      },
    },
    circle: {
      ...commonStyles,
      '&:before': {
        content: '""',
        display: 'inherit',
        height: 12,
        width: 12,
        borderRadius: '50%',
        backgroundColor: ({ enabled }) =>
          enabled ? theme.palette.green.main : theme.palette.red.main,
      },
    },
    vmhaStatusTooltipwrapper: {
      width: 'fit-content',
    },
  }
})
